/**
 * Onglet « Journal » — écran d'accueil de l'application (specs §6.2).
 *
 * Ordre de priorité visuelle imposé par les specs, et respecté ici de haut en
 * bas :
 *   1. bandeau de restant, lisible sans interaction  → `MacroBanner`
 *   2. liste des repas repliables, sous-total + objectif propre → `MealCard`
 *   3. bouton d'ajout accessible depuis chaque repas → `MealCard` puis `EntrySheet`
 *   4. navigation jour précédent / suivant / accès à une date → `DayNav`
 *
 * ⚠️ La feuille d'ajout est **montée en permanence** et seulement masquée
 * (`open`), au lieu d'être créée à l'ouverture : c'est la lecture concrète de
 * « l'écran d'ajout est préchargé dès l'affichage de l'onglet » (§6.2). Aucun
 * code n'est chargé ni aucun composant construit au moment du tap. Elle est
 * **hors de la piste** du pager, pour ne pas glisser avec la journée.
 *
 * ⚠️ La journée est créée paresseusement **par la lecture** (§4.5) : afficher
 * une date suffit à la matérialiser. C'est voulu, et c'est ce qui permet de
 * naviguer dans le passé sans préparation. Voir `dayQueryOptions` pour la
 * conséquence sur le préchargement du pager.
 *
 * ⚠️ `DayNav` est dans l'en-tête **collant**, donc hors de la piste : la date
 * reste à l'écran quand on défile, et n'est pas emportée par le glissement. Elle
 * change au moment où la journée bascule, pas pendant le geste.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * REFONTE « NOURRI »
 * ══════════════════════════════════════════════════════════════════════════
 *
 * L'habillage suit l'écran « Journal » du prototype : navigation de date entre
 * deux boutons ronds, carte-héros à anneau, fiches repas, ligne « nouveau
 * repas », ligne de modèle en pied. **Aucun des quatre points de priorité ne
 * change de place**, et la logique — pager, création paresseuse, aperçu
 * optimiste, feuilles — est intacte.
 *
 * ⚠️ Les trois notes d'état (lecture en échec, erreur de mutation, saisie en
 * attente de réseau) restent celles de l'ancienne bibliothèque. Ce ne sont pas
 * des éléments du prototype — il n'en montre aucun — mais des affordances de
 * fonctionnement, et le pont de `tokens-nourri.css` les tient dans la palette
 * de la refonte.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import type { DayDTO, LogEntryDTO } from '../../../../shared/types';
import { readError } from '../../api/client';
import { fetchTemplates } from '../../api/templates';
import { fetchWeight, weightKey } from '../../api/weight';
import {
  Button,
  Card,
  GlyphChevronLeft,
  GlyphChevronRight,
  GlyphTrend,
} from '../../components/nourri';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { ErrorNote, Hint, PendingNote } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { dayQueryOptions, useDay } from '../../hooks/useDay';
import { SETTLE_MS, useDayPager } from '../../hooks/useDayPager';
import { browserToday, formatDayLabel, shiftDate } from '../../lib/dates';
import { DayPeek } from './DayPeek';
import { EntrySheet } from './EntrySheet';
import { MacroBanner } from './MacroBanner';
import { MealCard } from './MealCard';
import { NewMealSheet } from './NewMealSheet';
import { TemplateSheet } from './TemplateSheet';

/** Cible ouverte par la feuille : un repas, et éventuellement une entrée à corriger. */
interface SheetTarget {
  mealId: number;
  mealName: string;
  entry?: LogEntryDTO;
}

/**
 * Plage interrogée pour savoir si la pesée du jour est faite.
 *
 * 🔴 **La même que celle par défaut de l'écran Progression, et c'est tout
 * l'intérêt.** Les deux écrans partagent alors la clé de cache : ouvrir le
 * journal n'ajoute aucune requête à qui vient de consulter Progression, et
 * inversement le journal réchauffe le cache de Progression. Une plage plus
 * courte aurait été une seconde entrée de cache, donc un second aller-retour
 * pour la même information.
 */
const PESEE_RANGE = '90d' as const;

/**
 * Rappel discret : rien n'a été pesé aujourd'hui.
 *
 * 🔴 **Neutre, pas alarmant.** L'application décrit, elle ne corrige pas et ne
 * juge pas (specs §6.2, §5.3) : c'est un rappel qu'un geste quotidien n'a pas
 * encore été fait, pas une faute. D'où une surface secondaire et un renvoi, là
 * où un bandeau rouge dirait qu'il s'est passé quelque chose de mauvais.
 *
 * ⚠️ **Seulement sur la journée d'aujourd'hui.** En feuilletant le journal
 * d'avant-hier on n'a rien à faire de la pesée du jour — et surtout, se peser
 * pour hier n'a plus de sens : c'est l'historique de Progression qui répare un
 * oubli, pas ce bandeau.
 *
 * ⚠️ **Rien tant que la lecture n'a pas abouti.** Hors ligne ou pendant le
 * premier chargement, `data` est indéfini : afficher le rappel « par défaut »
 * reviendrait à annoncer une pesée manquante qu'on n'a pas vérifiée.
 */
function PeseeNote({ date }: { date: string }) {
  const weight = useQuery({
    queryKey: weightKey(PESEE_RANGE),
    queryFn: () => fetchWeight(PESEE_RANGE),
    enabled: date === browserToday(),
  });

  if (date !== browserToday()) return null;
  if (!weight.data || weight.data.todayEntry !== null) return null;

  return (
    <Link to="/progression" className="n-nudge">
      <span className="n-nudge__icon" aria-hidden="true">
        <GlyphTrend size={16} />
      </span>
      <span className="n-nudge__text">Pas encore de pesée aujourd’hui</span>
      <span className="n-nudge__cta">Saisir</span>
    </Link>
  );
}

/** Prototype `.date-nav` — deux boutons ronds encadrant la date. */
function DayNav({ date, onGo }: { date: string; onGo: (date: string) => void }) {
  return (
    <div className="n-datenav">
      <Button
        variant="secondary"
        shape="circle"
        aria-label="Jour précédent"
        onClick={() => {
          onGo(shiftDate(date, -1));
        }}
      >
        <GlyphChevronLeft />
      </Button>

      {/*
        Accès direct à une date : le champ natif est superposé au libellé, en
        transparence. Toucher la date ouvre donc le sélecteur iOS — le geste le
        plus rapide possible — sans afficher deux fois la même date, et sans
        exposer un champ sous 16 px, qui ferait zoomer Safari à la mise au point.
      */}
      <label className="n-datenav__label">
        <span className="n-datenav__day">{formatDayLabel(date)}</span>
        <span className="n-datenav__sub">changer de date</span>
        <input
          type="date"
          value={date}
          onChange={(event) => {
            if (event.target.value !== '') onGo(event.target.value);
          }}
          aria-label="Choisir une date"
          className="n-datenav__input"
        />
      </label>

      <Button
        variant="secondary"
        shape="circle"
        aria-label="Jour suivant"
        onClick={() => {
          onGo(shiftDate(date, 1));
        }}
      >
        <GlyphChevronRight />
      </Button>
    </div>
  );
}

/**
 * Journée créée avant qu'aucun modèle n'existe (tout premier lancement).
 * On propose d'en appliquer un plutôt que de laisser un écran à zéro sans
 * explication.
 */
function NeedsTemplateNote({ onApply }: { onApply: (templateId: number) => void }) {
  const templates = useQuery({ queryKey: ['templates'], queryFn: fetchTemplates });

  return (
    <Card>
      <div className="n-eyebrow">Aucun objectif pour cette journée</div>
      {templates.data && templates.data.length > 0 ? (
        <>
          <p className="n-hint" style={{ marginTop: 10 }}>
            Appliquer un modèle en recopie les objectifs et les repas, ici et maintenant.
          </p>
          <div className="n-chip-row" style={{ marginTop: 12 }}>
            {templates.data.map((template) => (
              <Button
                key={template.id}
                variant="secondary"
                onClick={() => {
                  onApply(template.id);
                }}
              >
                {template.name}
              </Button>
            ))}
          </div>
        </>
      ) : (
        <p className="n-hint" style={{ marginTop: 10 }}>
          Créer un modèle de journée dans l’onglet Bibliothèque : il portera les objectifs
          P/G/L/kcal et la liste des repas.
        </p>
      )}
    </Card>
  );
}

/**
 * Ligne discrète sous les repas : quel modèle porte cette journée, et de quoi
 * en changer. Prototype `.template-row`.
 *
 * Discrète parce que c'est un geste occasionnel (specs §4 : « tout ce qui est
 * occasionnel peut être enfoui »), mais présente parce que le planning ne peut
 * plus rien pour une journée déjà née (§4.5).
 */
function TemplateRow({ day, onOpen }: { day: DayDTO; onOpen: () => void }) {
  const templates = useQuery({ queryKey: ['templates'], queryFn: fetchTemplates });
  const name = templates.data?.find((template) => template.id === day.sourceTemplateId)?.name;

  return (
    <button type="button" onClick={onOpen} className="n-template-row">
      <span className="n-template-row__what">
        Modèle : <b>{name ?? (day.sourceTemplateId === null ? 'aucun' : 'supprimé depuis')}</b>
      </span>
      <span className="n-template-row__cta">Changer</span>
    </button>
  );
}

export function TodayScreen() {
  const params = useParams<{ date?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selector = params.date ?? browserToday();

  const {
    day,
    error,
    isSaving,
    confirming,
    mutationError,
    pendingSync,
    addEntry,
    editEntry,
    removeEntry,
    adjustEntry,
    addMeal,
    renameMeal,
    setMealTargets,
    removeMeal,
    setMealFavorite,
    copyMealHere,
    applyTemplateHere,
    dismissError,
  } = useDay(selector);

  const [target, setTarget] = useState<SheetTarget | null>(null);
  /*
   * ⚠️ **Rien d'autre que l'ouverture n'est retenu ici** : le nom et les quatre
   * cibles vivent dans `NewMealSheet`, donc dans un contenu que `Sheet`
   * démonte à la fermeture. C'est ce qui remet le formulaire à zéro sans
   * qu'aucun chemin de sortie n'ait à y penser.
   */
  const [addMealOpen, setAddMealOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);

  const goTo = (date: string) => {
    // Une erreur affichée porte sur la journée qu'on quitte : la laisser
    // traîner sur la suivante serait trompeur.
    dismissError();
    void navigate(`/jour/${date}`);
  };

  // Date affichée dès le premier rendu, avant même la réponse du serveur : sans
  // cela l'en-tête apparaîtrait après coup et tout l'écran sauterait.
  const shownDate = day?.date ?? params.date ?? browserToday();
  const neighbour = (direction: 'prev' | 'next') =>
    shiftDate(shownDate, direction === 'next' ? 1 : -1);

  const pager = useDayPager({
    /*
     * 🔴 **La date de la ROUTE, pas celle des données.** C'est ce qui dit au
     * pager que la navigation a abouti et qu'il peut recaler la piste (cf.
     * `useDayPager`). `shownDate` retomberait sur les données, qui arrivent plus
     * tard quand la journée n'était pas en cache — le recalage attendrait pour
     * rien, et on regarderait le panneau voisin plus longtemps que nécessaire.
     */
    date: selector,
    onCommit: (direction) => {
      goTo(neighbour(direction));
    },
    onPeek: (direction) => {
      void queryClient.prefetchQuery(dayQueryOptions(neighbour(direction)));
    },
  });

  const body = () => {
    /*
     * 🔴 **Les données d'abord, l'erreur ensuite — l'ordre inverse était le
     * défaut.** Cette ligne testait `error || !day` : hors ligne, un cache
     * parfaitement valide était masqué par « Journée indisponible : Réseau
     * indisponible », parce que TanStack Query passe en `error` dès qu'un
     * rafraîchissement échoue, **même avec des données**. Le hors ligne était en
     * place et l'écran refusait de s'en servir. Trouvé à l'usage sur l'appareil,
     * pas au banc — voir `readError` dans `api/client.ts`, où le raisonnement et
     * la raison de l'angle mort du banc sont consignés.
     */
    const lecture = readError({ error, data: day });
    if (lecture) return <ReadNote error={lecture} quoi="cette journée" />;
    // ⚠️ `isPending` n'est plus consulté : ce qui décide de l'affichage est la
    // présence de données, pas l'état de la requête qui les a apportées. Une
    // requête en vol par-dessus un cache valide ne doit rien masquer.
    if (!day) return <Hint>Chargement de la journée…</Hint>;

    return (
      <div className="n-page">
        {/* 1. Bandeau de restant — l'information la plus consultée. */}
        <MacroBanner
          targets={day.targets}
          consumed={day.consumed}
          remaining={day.remaining}
          kcalGap={day.kcalGap}
          confirming={confirming}
          /*
            🔴 **Pas d'animation de remplissage quand la journée arrive par un
            glissement** *(31/08/2026, demandé après usage)*. `goTo()` sert aux
            deux chemins et navigue vers la même route, donc le bandeau n'est
            jamais démonté : ses valeurs changent sur un élément qui persiste et
            la transition rejoue. Aux flèches c'est ce qu'on veut ; au
            glissement elle se superpose au mouvement de la piste.

            ⚠️ `pager.swiped` est vrai au rendu même où la nouvelle date arrive,
            et **retombe tout seul** au contact suivant ou à 1 s — donc la
            saisie qui suit retrouve son animation sans qu'on ait à réarmer
            quoi que ce soit.
          */
          still={pager.swiped}
        />

        {/*
          ⚠️ **Sous le récapitulatif, au-dessus des repas.** C'est la première
          chose qu'on lit après le restant, et elle disparaît dès que la pesée
          est faite : la placer plus bas reviendrait à ne jamais la voir, plus
          haut à faire passer un rappel occasionnel devant l'information la plus
          consultée de l'application (specs §6.2).
        */}
        <PeseeNote date={shownDate} />

        {mutationError && <ErrorNote>{mutationError.message}</ErrorNote>}

        {/*
          ⚠️ **Neutre, et sous le bandeau de restant** (V2 §3.5). La saisie est
          là, le restant a bougé : ce qui manque n'est pas la donnée mais son
          accusé de réception. Un bandeau rouge dirait le contraire.

          🔴 **Le message a changé à l'étape 3, et il fallait qu'il change.** Il
          annonçait « elle sera à ressaisir une fois la connexion revenue » —
          vrai tant qu'aucune file n'existait, faux depuis qu'il y en a une.
          Laisser cette phrase aurait été le pire des cas : l'utilisateur
          ressaisirait une entrée déjà en file, et en obtiendrait deux.

          ⚠️ Elle dit aussi **quand** ça partira, parce que la réponse n'est pas
          « toute seule, n'importe quand » : Background Sync n'existe pas sur
          Safari iOS, la file vit dans la page (§3.3). Une promesse d'envoi en
          arrière-plan serait une promesse que la plateforme ne tient pas.
        */}
        {pendingSync && (
          <PendingNote>
            Pas de connexion : cette saisie est enregistrée sur cet appareil et
            partira à la prochaine ouverture de l’application avec du réseau.
            Rien à ressaisir.
          </PendingNote>
        )}

        {/*
          ⚠️ Seul le cas « journée sans objectif » reste en tête : il n'y a rien
          d'autre à lire sur la page, et c'est une invitation à agir, pas un
          réglage. La ligne « Modèle : … » a été déplacée **tout en bas**
          (demandé après usage) — voir plus loin.
        */}
        {day.needsTemplate && <NeedsTemplateNote onApply={applyTemplateHere} />}

        {/* 2 et 3. Liste des repas, chacun avec son bouton d'ajout. */}
        {day.meals.length === 0 && !day.needsTemplate && (
          <p className="n-empty">Aucun repas dans cette journée. En ajouter un ci-dessous.</p>
        )}

        {day.meals.map((meal, index) => (
          <MealCard
            key={meal.id}
            meal={meal}
            /* Décoratif : la teinte de la pastille quand le nom ne la donne pas. */
            index={index}
            onAdd={() => {
              setTarget({ mealId: meal.id, mealName: meal.name });
            }}
            onEditEntry={(entry) => {
              setTarget({ mealId: meal.id, mealName: meal.name, entry });
            }}
            onRemoveEntry={removeEntry}
            onRename={(name) => {
              renameMeal(meal.id, name);
            }}
            onSetTargets={(targets) => {
              setMealTargets(meal.id, targets);
            }}
            onRemove={() => {
              removeMeal(meal.id);
            }}
            onToggleFavorite={(isFavorite) => {
              setMealFavorite(meal.id, isFavorite);
            }}
          />
        ))}

        {/*
          Ajout de repas libre — sans effet sur le modèle source (specs §6.2).

          🔴 **Un bouton, puis une feuille** *(04/09/2026, second retour du même
          jour)*. La veille, c'était un champ en pied d'écran et un dépliement
          de quatre cases sous lui : ça tenait tant qu'un repas n'avait qu'un
          nom, ça ne tient plus dès qu'il en a cinq — le pied de page se met à
          bouger à la frappe, et l'ajout devient un formulaire posé au milieu de
          la liste des repas.

          ⚠️ **Le bouton reste à la même place que le champ qu'il remplace**, en
          pied de liste : c'est là qu'on va chercher « ajouter un repas », et
          déplacer le geste en même temps qu'on change sa forme aurait fait deux
          choses à réapprendre au lieu d'une.
        */}
        <Button
          variant="ghost"
          shape="rounded"
          block
          onClick={() => {
            setAddMealOpen(true);
          }}
        >
          + Ajouter un repas
        </Button>

        {/*
          🔴 **Le modèle de la journée, tout en bas** — demandé après usage.

          Il était sous le bandeau de restant, en deuxième position de l'écran
          le plus consulté de l'application. Or changer le modèle d'une journée
          est un geste **rare** : on le fait quand la journée ne s'est pas
          passée comme prévu, une fois de temps en temps, jamais plusieurs fois
          par jour comme on ajoute un aliment. Le §4 des specs range ce genre de
          geste sans ambiguïté : « tout ce qui est occasionnel peut être
          enfoui ».

          Il vient donc après l'ajout de repas — les deux actions qui
          restructurent la journée sont désormais voisines, et le chemin
          critique (lire le restant, ajouter au bon repas) n'est plus coupé en
          deux par un réglage.
        */}
        {!day.needsTemplate && (
          <TemplateRow
            day={day}
            onOpen={() => {
              setTemplateOpen(true);
            }}
          />
        )}

        {/*
          ⚠️ Toujours dans le DOM, seulement transparent : un élément qui
          apparaît et disparaît décalerait la page d'une ligne à chaque
          enregistrement.
        */}
        <p className="n-saving" style={{ opacity: isSaving ? 1 : 0 }}>
          Enregistrement…
        </p>
      </div>
    );
  };

  return (
    <>
      {/* 4. Navigation — en tête, et elle y reste quand on défile. */}
      <ScreenHeader>
        <DayNav date={shownDate} onGo={goTo} />
      </ScreenHeader>

      {/*
        Le cadre découpe et ne bouge pas ; la piste glisse à l'intérieur. Réunir
        les deux rôles sur un seul élément ferait disparaître les journées
        voisines au moment même où elles doivent entrer — cf. `styles/surfaces.css`.
      */}
      <div className="day-viewport">
        <div
          className="day-track"
          {...pager.handlers}
          style={{
            transform: `translate3d(${String(pager.offset)}px, 0, 0)`,
            // Aucune transition pendant le geste : la journée doit coller au
            // doigt. Elle ne se recale en douceur qu'au relâchement.
            transition: pager.animating
              ? `transform ${String(SETTLE_MS)}ms cubic-bezier(.22,.61,.36,1)`
              : 'none',
          }}
        >
          {pager.peek === 'prev' && <DayPeek date={neighbour('prev')} side="prev" />}
          {body()}
          {pager.peek === 'next' && <DayPeek date={neighbour('next')} side="next" />}
        </div>
      </div>

      <NewMealSheet
        open={addMealOpen}
        onClose={() => {
          setAddMealOpen(false);
        }}
        onAdd={addMeal}
      />

      <TemplateSheet
        open={templateOpen}
        currentTemplateId={day?.sourceTemplateId ?? null}
        onClose={() => {
          setTemplateOpen(false);
        }}
        onPick={applyTemplateHere}
      />

      {/*
        Montée en permanence, masquée quand `open` est faux : c'est ce qui rend
        l'ouverture instantanée (§6.2). `target` porte le repas visé.
      */}
      <EntrySheet
        open={target !== null}
        mealId={target?.mealId}
        mealName={target?.mealName ?? ''}
        entry={target?.entry}
        onClose={() => {
          setTarget(null);
        }}
        onSubmit={(input, preview) => {
          if (!target) return;
          // L'aperçu vient de l'écran d'ajout, qui sait ce qu'il ajoute ; il ne
          // part jamais au serveur (§5.1), il ne sert qu'à faire bouger le
          // bandeau de restant tout de suite (§6.2).
          if (target.entry) editEntry(target.entry.id, input, preview);
          else addEntry(target.mealId, input, preview);
        }}
        onAdjust={(lines) => {
          // Correction d'un bloc recette (specs §6.5-d) : route dédiée, qui ne
          // touche ni à la recette source ni aux autres entrées.
          if (target?.entry) adjustEntry(target.entry.id, lines);
        }}
        onCopyMeal={(sourceMealId) => {
          // Recopie d'un repas déjà composé. Pas d'aperçu local : le serveur
          // seul sait quelles lignes il va écrire (§5.1), et le geste remplace
          // une dizaine de saisies — un aller-retour y est invisible.
          if (target) copyMealHere(target.mealId, sourceMealId);
        }}
      />
    </>
  );
}
